import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ListTodo, Plus, Trash2, Pencil, Loader2, ArrowUp, ArrowDown, ArrowUpDown, CheckCircle2, Circle, AlertTriangle, Flag, Wrench, ChevronDown, ChevronUp, Calendar, User, Building2, Filter } from 'lucide-react';
import { CreateTaskDialog, EditTaskDialog, DeleteTaskDialog } from '@/components/dialogs';

import { API } from '../utils/api';

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'High', label: 'High', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertTriangle },
  { value: 'Low', label: 'Low', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Flag },
  { value: 'Maintenance', label: 'Maintenance', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Wrench },
];

function Tasks() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [llcs, setLlcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [expandedMobileTask, setExpandedMobileTask] = useState(null);
  const [filterLlcId, setFilterLlcId] = useState('all');
  const [toMeCollapsed, setToMeCollapsed] = useState(false);
  const [byMeCollapsed, setByMeCollapsed] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    llc_id: '',
    llc_ids: [],  // For multi-select in create dialog
    task_ids: [], // For bulk edit
    assigned_to_id: '',
    task_description: '',
    date_needed: '',
    priority: 'Low'
  });
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(() => {
    return localStorage.getItem('tasksSortColumn') || 'date_needed';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('tasksSortDirection') || 'asc';
  });

  // Fetch data
  const fetchData = async () => {
    try {
      // Fetch tasks first (required)
      const tasksRes = await axios.get(`${API}/tasks`, { headers: getAuthHeaders() });
      setTasks(tasksRes.data);
      
      // Fetch assignees for task creation (uses tasks/assignees endpoint)
      try {
        const assigneesRes = await axios.get(`${API}/tasks/assignees`, { headers: getAuthHeaders() });
        setUsers(assigneesRes.data);
      } catch (e) {
        console.log('Could not fetch assignees list');
      }
      
      // Fetch LLCs for task creation (uses tasks/llcs endpoint)
      try {
        const llcsRes = await axios.get(`${API}/tasks/llcs`, { headers: getAuthHeaders() });
        setLlcs(llcsRes.data);
      } catch (e) {
        console.log('Could not fetch LLCs list');
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      const newDir = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDir);
      localStorage.setItem('tasksSortDirection', newDir);
    } else {
      setSortColumn(column);
      setSortDirection('asc');
      localStorage.setItem('tasksSortColumn', column);
      localStorage.setItem('tasksSortDirection', 'asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-3 w-3 text-slate-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  // Priority sort order (High first, then Low, then Maintenance)
  const priorityOrder = { 'High': 0, 'Low': 1, 'Maintenance': 2 };

  // Filter tasks by LLC and completion status
  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (filterLlcId && filterLlcId !== 'all') {
      result = result.filter(task => task.llc_id === filterLlcId);
    }
    if (!showCompleted) {
      result = result.filter(task => !task.date_completed);
    }
    return result;
  }, [tasks, filterLlcId, showCompleted]);

  // Split tasks into panels (match by ID or name for backup/restore resilience)
  const assignedToMe = useMemo(() => {
    const uid = currentUser?.id;
    const uname = currentUser?.name;
    return filteredTasks.filter(t => t.assigned_to_id === uid || t.assigned_to_name === uname);
  }, [filteredTasks, currentUser]);

  const assignedByMe = useMemo(() => {
    const uid = currentUser?.id;
    const uname = currentUser?.name;
    return filteredTasks.filter(t =>
      (t.created_by_id === uid || t.created_by_name === uname) &&
      t.assigned_to_id !== uid && t.assigned_to_name !== uname
    );
  }, [filteredTasks, currentUser]);

  // Sort a given task list
  const sortTaskList = (taskList) => {
    return [...taskList].sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'llc_name':
          aVal = a.llc_name?.toLowerCase() || '';
          bVal = b.llc_name?.toLowerCase() || '';
          break;
        case 'assigned_to_name':
          aVal = a.assigned_to_name?.toLowerCase() || '';
          bVal = b.assigned_to_name?.toLowerCase() || '';
          break;
        case 'created_by_name':
          aVal = a.created_by_name?.toLowerCase() || '';
          bVal = b.created_by_name?.toLowerCase() || '';
          break;
        case 'task_description':
          aVal = a.task_description?.toLowerCase() || '';
          bVal = b.task_description?.toLowerCase() || '';
          break;
        case 'date_needed':
          aVal = a.date_needed || '';
          bVal = b.date_needed || '';
          break;
        case 'date_completed':
          aVal = a.date_completed || 'zzz';
          bVal = b.date_completed || 'zzz';
          break;
        case 'priority':
          aVal = priorityOrder[a.priority] ?? 1;
          bVal = priorityOrder[b.priority] ?? 1;
          break;
        default:
          aVal = a[sortColumn] || '';
          bVal = b[sortColumn] || '';
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Desktop: Sorted by user-selected column
  const sortedTasks = useMemo(() => sortTaskList(filteredTasks), [filteredTasks, sortColumn, sortDirection]);

  // Render desktop table for a task list
  const renderDesktopTable = (taskList, showAssignedTo = true, showAssignedBy = true) => {
    const sorted = sortTaskList(taskList);
    return (
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-800 text-white">
              <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[75px]" onClick={() => handleSort('priority')}>
                <div className="flex items-center">Priority<SortIcon column="priority" /></div>
              </TableHead>
              <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[120px]" onClick={() => handleSort('llc_name')}>
                <div className="flex items-center">LLC Name<SortIcon column="llc_name" /></div>
              </TableHead>
              {showAssignedTo && (
                <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[110px]" onClick={() => handleSort('assigned_to_name')}>
                  <div className="flex items-center">Assigned To<SortIcon column="assigned_to_name" /></div>
                </TableHead>
              )}
              {showAssignedBy && (
                <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[110px]" onClick={() => handleSort('created_by_name')}>
                  <div className="flex items-center">Assigned By<SortIcon column="created_by_name" /></div>
                </TableHead>
              )}
              <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors min-w-[250px]" onClick={() => handleSort('task_description')}>
                <div className="flex items-center">Task<SortIcon column="task_description" /></div>
              </TableHead>
              <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[95px]" onClick={() => handleSort('date_needed')}>
                <div className="flex items-center">Date Needed<SortIcon column="date_needed" /></div>
              </TableHead>
              <TableHead className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors w-[95px]" onClick={() => handleSort('date_completed')}>
                <div className="flex items-center">Completed<SortIcon column="date_completed" /></div>
              </TableHead>
              <TableHead className="w-[50px] text-white font-semibold text-center">Status</TableHead>
              <TableHead className="text-white font-semibold w-[70px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((task) => {
              const isOverdue = !task.date_completed && new Date(task.date_needed) < new Date();
              const canDelete = task.created_by_id === currentUser?.id;
              return (
                <TableRow key={task.id} className={`table-row-hover ${task.date_completed ? 'bg-emerald-50/50' : isOverdue ? 'bg-rose-50/50' : ''}`}>
                  <TableCell>
                    {(() => {
                      const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === task.priority) || PRIORITY_OPTIONS[1];
                      const PriorityIcon = priorityConfig.icon;
                      return (
                        <Badge variant="outline" className={`${priorityConfig.color} text-xs`}>
                          <PriorityIcon className="w-3 h-3 mr-1" />{priorityConfig.label}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="font-medium text-sm">
                    <Link to={`/llc/${task.llc_id}`} className="text-amber-700 hover:text-amber-900 hover:underline transition-colors" data-testid={`task-llc-link-${task.id}`}>
                      {task.llc_name}
                    </Link>
                  </TableCell>
                  {showAssignedTo && <TableCell className="text-sm">{task.assigned_to_name}</TableCell>}
                  {showAssignedBy && <TableCell className="text-sm text-slate-600">{task.created_by_name}</TableCell>}
                  <TableCell className={`whitespace-normal text-sm ${task.date_completed ? 'line-through text-slate-400' : ''}`}>{task.task_description}</TableCell>
                  <TableCell className={`text-sm whitespace-nowrap ${isOverdue && !task.date_completed ? 'text-rose-600 font-semibold' : ''}`}>{formatDate(task.date_needed)}</TableCell>
                  <TableCell className={`text-sm whitespace-nowrap ${task.date_completed ? 'text-emerald-600' : 'text-slate-400'}`}>{formatDate(task.date_completed)}</TableCell>
                  <TableCell className="text-center">
                    <button onClick={() => handleToggleComplete(task)} className="hover:scale-110 transition-transform" title={task.date_completed ? 'Mark incomplete' : 'Mark complete'}>
                      {task.date_completed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-400 hover:text-emerald-500" />}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(task)} data-testid={`edit-task-${task.id}`}>
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { setSelectedTask(task); setDeleteDialogOpen(true); }} data-testid={`delete-task-${task.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render mobile cards for a task list
  const renderMobileCards = (taskList) => {
    const sorted = [...taskList].sort((a, b) => {
      const aPriority = priorityOrder[a.priority] ?? 1;
      const bPriority = priorityOrder[b.priority] ?? 1;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (a.date_needed || 'zzz').localeCompare(b.date_needed || 'zzz');
    });
    return (
      <div className="md:hidden space-y-3">
        {sorted.map((task) => {
          const isOverdue = !task.date_completed && new Date(task.date_needed) < new Date();
          const canDelete = task.created_by_id === currentUser?.id;
          const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === task.priority) || PRIORITY_OPTIONS[1];
          const PriorityIcon = priorityConfig.icon;
          const isExpanded = expandedMobileTask === task.id;
          return (
            <div key={task.id} className={`border rounded-lg overflow-hidden ${task.date_completed ? 'bg-emerald-50/50 border-emerald-200' : isOverdue ? 'bg-rose-50/50 border-rose-200' : 'bg-white border-slate-200'}`} data-testid={`mobile-task-${task.id}`}>
              <div className="p-3 cursor-pointer" onClick={() => setExpandedMobileTask(isExpanded ? null : task.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`${priorityConfig.color} text-xs shrink-0`}><PriorityIcon className="w-3 h-3 mr-1" />{priorityConfig.label}</Badge>
                      <Link to={`/llc/${task.llc_id}`} onClick={(e) => e.stopPropagation()} className="text-amber-700 hover:text-amber-900 font-medium text-sm truncate">{task.llc_name}</Link>
                    </div>
                    <p className={`text-sm ${task.date_completed ? 'line-through text-slate-400' : 'text-slate-800'}`}>{task.task_description}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); handleToggleComplete(task); }} className="hover:scale-110 transition-transform">
                      {task.date_completed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-400" />}
                    </button>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t border-slate-100 bg-slate-50/50">
                  <div className="grid grid-cols-2 gap-2 py-2 text-sm">
                    <div><span className="text-slate-500">Assigned To:</span><p className="font-medium">{task.assigned_to_name}</p></div>
                    <div><span className="text-slate-500">Assigned By:</span><p className="text-slate-600">{task.created_by_name}</p></div>
                    <div><span className="text-slate-500">Date Needed:</span><p className={`font-medium ${isOverdue && !task.date_completed ? 'text-rose-600' : ''}`}>{formatDate(task.date_needed)}</p></div>
                    <div><span className="text-slate-500">Completed:</span><p className={task.date_completed ? 'text-emerald-600 font-medium' : 'text-slate-400'}>{formatDate(task.date_completed) || '-'}</p></div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(task)}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                    {canDelete && (
                      <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setSelectedTask(task); setDeleteDialogOpen(true); }}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render empty state
  const renderEmpty = (message) => (
    <div className="text-center py-8">
      <ListTodo className="w-10 h-10 text-slate-400 mx-auto mb-3" />
      <p className="text-slate-500 text-sm">{message}</p>
    </div>
  );
  const sortedLlcs = useMemo(() => {
    return [...llcs].sort((a, b) => a.name.localeCompare(b.name));
  }, [llcs]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      llc_id: '',
      llc_ids: [],
      assigned_to_id: '',
      task_description: '',
      date_needed: '',
      priority: 'Low'
    });
  };

  // Toggle LLC selection for bulk create
  const toggleLlcSelection = (llcId) => {
    setFormData(prev => ({
      ...prev,
      llc_ids: prev.llc_ids.includes(llcId)
        ? prev.llc_ids.filter(id => id !== llcId)
        : [...prev.llc_ids, llcId]
    }));
  };

  // Select/deselect all LLCs
  const toggleAllLlcs = () => {
    setFormData(prev => ({
      ...prev,
      llc_ids: prev.llc_ids.length === llcs.length ? [] : llcs.map(l => l.id)
    }));
  };

  // Create task (supports bulk creation)
  const handleCreate = async () => {
    if (formData.llc_ids.length === 0 || !formData.assigned_to_id || !formData.task_description || !formData.date_needed) {
      toast.error('Please fill all fields and select at least one LLC');
      return;
    }
    
    setFormLoading(true);
    try {
      if (formData.llc_ids.length === 1) {
        // Single task creation
        await axios.post(`${API}/tasks`, {
          llc_id: formData.llc_ids[0],
          assigned_to_id: formData.assigned_to_id,
          task_description: formData.task_description,
          date_needed: formData.date_needed,
          priority: formData.priority
        }, { headers: getAuthHeaders() });
        toast.success('Task created');
      } else {
        // Bulk task creation
        await axios.post(`${API}/tasks/bulk`, {
          llc_ids: formData.llc_ids,
          assigned_to_id: formData.assigned_to_id,
          task_description: formData.task_description,
          date_needed: formData.date_needed,
          priority: formData.priority
        }, { headers: getAuthHeaders() });
        toast.success(`${formData.llc_ids.length} tasks created`);
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setFormLoading(false);
    }
  };

  // Edit task (supports bulk editing)
  const handleEdit = async () => {
    if (!selectedTask) return;
    
    setFormLoading(true);
    try {
      const taskIdsToUpdate = formData.task_ids || [selectedTask.id];
      
      if (taskIdsToUpdate.length > 1) {
        // Bulk update all related tasks
        await axios.put(`${API}/tasks/bulk`, {
          task_ids: taskIdsToUpdate,
          assigned_to_id: formData.assigned_to_id,
          task_description: formData.task_description,
          date_needed: formData.date_needed,
          date_completed: formData.date_completed || null,
          priority: formData.priority
        }, { headers: getAuthHeaders() });
        toast.success(`${taskIdsToUpdate.length} tasks updated`);
      } else {
        await axios.put(`${API}/tasks/${selectedTask.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Task updated');
      }
      setEditDialogOpen(false);
      setSelectedTask(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setFormLoading(false);
    }
  };

  // Delete task
  const handleDelete = async () => {
    if (!selectedTask) return;
    
    setFormLoading(true);
    try {
      await axios.delete(`${API}/tasks/${selectedTask.id}`, { headers: getAuthHeaders() });
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setFormLoading(false);
    }
  };

  // Toggle completion
  const handleToggleComplete = async (task) => {
    const newDateCompleted = task.date_completed ? null : new Date().toISOString().split('T')[0];
    
    try {
      await axios.put(`${API}/tasks/${task.id}`, { date_completed: newDateCompleted || '' }, { headers: getAuthHeaders() });
      toast.success(newDateCompleted ? 'Task marked complete' : 'Task marked incomplete');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Open edit dialog - find related tasks with same description/assignee/date
  const openEditDialog = (task) => {
    setSelectedTask(task);
    
    // Find all related tasks (same description, assignee, date_needed, priority)
    const relatedTasks = tasks.filter(t => 
      t.task_description === task.task_description &&
      t.assigned_to_id === task.assigned_to_id &&
      t.date_needed === task.date_needed &&
      t.priority === task.priority &&
      !t.date_completed  // Only include incomplete tasks
    );
    
    // Get the LLC IDs of all related tasks
    const relatedLlcIds = relatedTasks.map(t => t.llc_id);
    const relatedTaskIds = relatedTasks.map(t => t.id);
    
    setFormData({
      llc_id: task.llc_id,
      llc_ids: relatedLlcIds,
      task_ids: relatedTaskIds,  // Store task IDs for bulk update
      assigned_to_id: task.assigned_to_id,
      task_description: task.task_description,
      date_needed: task.date_needed,
      date_completed: task.date_completed || '',
      priority: task.priority || 'Low'
    });
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold text-slate-800">Task Management</h1>
          <p className="text-slate-600 mt-1">Manage and track tasks across your LLCs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2" data-testid="show-completed-toggle">
            <Switch id="show-completed-tasks" checked={showCompleted} onCheckedChange={setShowCompleted} />
            <Label htmlFor="show-completed-tasks" className="text-sm text-slate-600 cursor-pointer whitespace-nowrap">Show Completed</Label>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filterLlcId} onValueChange={setFilterLlcId}>
              <SelectTrigger className="w-[200px]" data-testid="llc-filter-select">
                <SelectValue placeholder="Filter by LLC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-500" />All LLCs</span>
                </SelectItem>
                {sortedLlcs.map((llc) => (
                  <SelectItem key={llc.id} value={llc.id}>
                    <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-amber-600" />{llc.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Panel: Assigned To Me */}
      <Card className="border-slate-200" data-testid="assigned-to-me-panel">
        <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setToMeCollapsed(p => !p)} data-testid="assigned-to-me-header">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-amber-600" />
                Assigned To Me
                <Badge variant="secondary" className="ml-1 text-xs">{assignedToMe.length}</Badge>
              </CardTitle>
              <CardDescription>
                {assignedToMe.length} task{assignedToMe.length !== 1 ? 's' : ''} assigned to you
                {filterLlcId !== 'all' && ' (filtered)'}
              </CardDescription>
            </div>
            {toMeCollapsed ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {!toMeCollapsed && (
          <CardContent className="pt-0">
            {assignedToMe.length === 0
              ? renderEmpty('No tasks assigned to you')
              : <>
                  {renderMobileCards(assignedToMe)}
                  {renderDesktopTable(assignedToMe, false, true)}
                </>
            }
          </CardContent>
        )}
      </Card>

      {/* Panel: Assigned By Me */}
      <Card className="border-slate-200" data-testid="assigned-by-me-panel">
        <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setByMeCollapsed(p => !p)} data-testid="assigned-by-me-header">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif flex items-center gap-2 text-lg">
                <ListTodo className="w-5 h-5 text-indigo-600" />
                Assigned By Me
                <Badge variant="secondary" className="ml-1 text-xs">{assignedByMe.length}</Badge>
              </CardTitle>
              <CardDescription>
                {assignedByMe.length} task{assignedByMe.length !== 1 ? 's' : ''} you assigned to others
                {filterLlcId !== 'all' && ' (filtered)'}
              </CardDescription>
            </div>
            {byMeCollapsed ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {!byMeCollapsed && (
          <CardContent className="pt-0">
            {assignedByMe.length === 0
              ? renderEmpty('No tasks assigned by you to others')
              : <>
                  {renderMobileCards(assignedByMe)}
                  {renderDesktopTable(assignedByMe, true, false)}
                </>
            }
          </CardContent>
        )}
      </Card>

      {/* Create Task Dialog */}
      <CreateTaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        formData={formData}
        setFormData={setFormData}
        llcs={llcs}
        users={users}
        priorityOptions={PRIORITY_OPTIONS}
        formLoading={formLoading}
        onSubmit={handleCreate}
        onToggleLlcSelection={toggleLlcSelection}
        onToggleAllLlcs={toggleAllLlcs}
      />

      {/* Edit Task Dialog */}
      <EditTaskDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        formData={formData}
        setFormData={setFormData}
        llcs={llcs}
        users={users}
        priorityOptions={PRIORITY_OPTIONS}
        formLoading={formLoading}
        onSubmit={handleEdit}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteTaskDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        formLoading={formLoading}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default Tasks;
